<script setup>
/* global axios */
// Gelen kutusu ayarlarinda agent secimi.
//
// Chatwoot'un "Temsilci botu" (Agent Bot) bolumunun yerini aliyor. O bolum
// konusmayi asistandan ALIP bir webhook'a devrediyordu; ikisi ayni kutuda
// birlikte calismiyordu. Agent ise tam tersi: asistan konusmayi yonetmeye
// devam ediyor, gerektiginde agent'i cagirip sonucu cumlesine katiyor.
// Siparis olusturmak, stok sormak, kargo takip etmek boyle yapiliyor.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = computed(() => route.params.accountId);
const adres = computed(
  () => `/api/v1/accounts/${accountId.value}/gelen-kutusu-agent/${props.inbox.id}`
);

const agentlar = ref([]);
const asistan = ref(null);
const ozellikAcik = ref(true);
const katalogAcik = ref(false);
const katalogUrunSayisi = ref(0);
const katalogIlk = ref(false);
const katalogKaynaklari = ref([]);
const katalogSecenekleri = ref([]);
const katalogKapsamSayisi = ref(0);
const katalogKaynakIlk = ref('');
const yukleniyor = ref(true);
const kaydediliyor = ref(false);
const hata = ref('');

// Kaydedilmemis degisiklik var mi - dugmeyi bosuna aktif tutmayalim.
const ilkHal = ref('');
const suankiHal = computed(() =>
  agentlar.value
    .filter(a => a.secili)
    .map(a => a.id)
    .sort()
    .join(',')
);
// Bos liste "hepsi" demek; siralamayi sabitleyerek karsilastiriyoruz.
const kaynakHali = computed(() => [...katalogKaynaklari.value].sort().join(','));

const degisti = computed(
  () =>
    suankiHal.value !== ilkHal.value ||
    katalogAcik.value !== katalogIlk.value ||
    kaynakHali.value !== katalogKaynakIlk.value
);

// Kaydedilmemis secim icin sayiyi istemcide hesapliyoruz: sunucudan
// gelen sayi son KAYDEDILEN hali yansitiyor ve kullanici kutucuk
// isaretlerken eski rakami goruyordu.
const onizlemeSayisi = computed(() => {
  if (!katalogSecenekleri.value.length) return katalogKapsamSayisi.value;

  const secili = katalogKaynaklari.value.length
    ? katalogSecenekleri.value.filter(s => katalogKaynaklari.value.includes(s.anahtar))
    : katalogSecenekleri.value;

  return secili.reduce((toplam, s) => toplam + (s.adet || 0), 0);
});

const kaynakAdi = secenek => {
  if (secenek.tur === 'yerel') return t('OMNUP_AGENT.KAYNAK_YEREL');
  if (secenek.tur === 'meta') return t('OMNUP_AGENT.KAYNAK_META');
  return secenek.ad;
};

const kaynakCevir = anahtar => {
  const liste = [...katalogKaynaklari.value];
  const yeri = liste.indexOf(anahtar);
  if (yeri === -1) liste.push(anahtar);
  else liste.splice(yeri, 1);
  katalogKaynaklari.value = liste;
};

const yukle = async () => {
  yukleniyor.value = true;
  hata.value = '';
  // Onceki kutunun durumu ekranda KALMAMALI: yukleme hata verirse
  // Kaydet, eski kutunun ayarlarini yeni kutuya yazardi.
  agentlar.value = [];
  katalogAcik.value = false;
  katalogKaynaklari.value = [];
  katalogSecenekleri.value = [];
  katalogKapsamSayisi.value = 0;
  ilkHal.value = '';
  katalogIlk.value = false;
  katalogKaynakIlk.value = '';
  try {
    const { data } = await axios.get(adres.value);
    agentlar.value = data.agentlar || [];
    asistan.value = data.asistan || null;
    ozellikAcik.value = data.ozellik_acik !== false;
    katalogAcik.value = Boolean(data.katalog_acik);
    katalogUrunSayisi.value = data.katalog_urun_sayisi || 0;
    katalogSecenekleri.value = data.katalog_secenekleri || [];
    katalogKaynaklari.value = data.katalog_kaynaklari || [];
    katalogKapsamSayisi.value = data.katalog_kapsam_sayisi || 0;
    katalogIlk.value = katalogAcik.value;
    katalogKaynakIlk.value = kaynakHali.value;
    ilkHal.value = suankiHal.value;
  } catch (e) {
    hata.value = e?.response?.data?.error || t('OMNUP_AGENT.HATA');
  } finally {
    yukleniyor.value = false;
  }
};

const kaydet = async () => {
  kaydediliyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.put(adres.value, {
      agent_idleri: agentlar.value.filter(a => a.secili).map(a => a.id),
      katalog_acik: katalogAcik.value,
      katalog_kaynaklari: katalogKaynaklari.value,
    });
    agentlar.value = data.agentlar || [];
    katalogAcik.value = Boolean(data.katalog_acik);
    katalogKaynaklari.value = data.katalog_kaynaklari || [];
    katalogKapsamSayisi.value = data.katalog_kapsam_sayisi || 0;
    katalogIlk.value = katalogAcik.value;
    katalogKaynakIlk.value = kaynakHali.value;
    ilkHal.value = suankiHal.value;
    useAlert(t('OMNUP_AGENT.KAYDEDILDI'));
  } catch (e) {
    hata.value = e?.response?.data?.error || t('OMNUP_AGENT.HATA');
  } finally {
    kaydediliyor.value = false;
  }
};

// Agentlar asistan bazinda degil hesap bazinda olusturuluyor; olusturma
// ekrani Tepegoz AI altinda. Buradan oraya goturuyoruz ki kullanici
// menude agent aramasin.
const olusturmayaGit = () => {
  router.push({
    name: 'captain_assistants_index',
    params: { accountId: accountId.value, navigationPath: 'captain_tools_index' },
  });
};

onMounted(yukle);

// Bilesen yeniden kurulmadan baska bir gelen kutusuna gecilebiliyor.
// Yeniden yuklemezsek ekranda eski kutunun secimi kalir ve Kaydet onu
// YENI kutuya yazar.
watch(() => props.inbox.id, yukle);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('OMNUP_AGENT.BASLIK') }}
      </h3>
      <p class="text-xs text-n-slate-11">{{ t('OMNUP_AGENT.ACIKLAMA') }}</p>
    </div>

    <div v-if="yukleniyor" class="flex justify-center p-4">
      <Spinner class="text-n-brand" />
    </div>

    <template v-else>
      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <!-- Katalog erisimi. Agentlarla ayni ekranda cunku ikisi de
           "asistan bu kutuda neye erisebilir" sorusunun parcasi. -->
      <div
        class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-solid border-n-weak"
      >
        <input
          id="omnup-katalog-acik"
          v-model="katalogAcik"
          type="checkbox"
          class="mt-0.5 shrink-0"
        />
        <label for="omnup-katalog-acik" class="flex flex-col gap-0.5 cursor-pointer">
          <span class="text-sm text-n-slate-12">
            {{ t('OMNUP_AGENT.KATALOG_BASLIK') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('OMNUP_AGENT.KATALOG_ACIKLAMA') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{
              katalogUrunSayisi
                ? t('OMNUP_AGENT.KATALOG_SAYI', { sayi: katalogUrunSayisi })
                : t('OMNUP_AGENT.KATALOG_BOS')
            }}
          </span>
          <span class="mt-1 text-xs text-n-slate-11">
            {{ t('OMNUP_AGENT.KATALOG_YAPABILIR') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('OMNUP_AGENT.KATALOG_YAPAMAZ') }}
          </span>
        </label>
      </div>

      <!-- Kaynak secimi. Urunler birden fazla yerden gelebiliyor (elle
           girilen, Meta, kendi API'niz) ve her kutuda ayni listeyi
           gostermek zorunda degiliz: toptan kutusu baska, perakende
           kutusu baska katalog gorebilir. Hicbiri isaretli degilse
           hepsi gosterilir. -->
      <div
        v-if="katalogAcik && katalogSecenekleri.length"
        class="flex flex-col gap-2 px-3 py-2.5 ml-6 rounded-lg bg-n-alpha-1"
      >
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('OMNUP_AGENT.KAYNAK_BASLIK') }}
        </span>
        <label
          v-for="secenek in katalogSecenekleri"
          :key="secenek.anahtar"
          class="flex items-center gap-2 text-xs cursor-pointer text-n-slate-11"
        >
          <input
            type="checkbox"
            class="shrink-0"
            :checked="katalogKaynaklari.includes(secenek.anahtar)"
            @change="kaynakCevir(secenek.anahtar)"
          />
          <span>{{ kaynakAdi(secenek) }}</span>
          <span class="text-n-slate-10">({{ secenek.adet }})</span>
        </label>
        <span class="text-xs text-n-slate-10">
          {{
            katalogKaynaklari.length
              ? t('OMNUP_AGENT.KAYNAK_SECILI', { sayi: onizlemeSayisi })
              : t('OMNUP_AGENT.KAYNAK_HEPSI')
          }}
        </span>
      </div>


      <p
        v-if="!ozellikAcik"
        class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
      >
        {{ t('OMNUP_AGENT.OZELLIK_KAPALI') }}
      </p>

      <template v-else>
        <p
          v-if="!asistan"
          class="px-3 py-2 text-xs rounded-lg bg-n-amber-9/10 text-n-amber-11"
        >
          {{ t('OMNUP_AGENT.ASISTAN_YOK') }}
        </p>

        <p
          v-if="!agentlar.length"
          class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
        >
          {{ t('OMNUP_AGENT.AGENT_YOK') }}
        </p>

        <ul v-else class="flex flex-col gap-2 m-0 list-none">
          <li
            v-for="agent in agentlar"
            :key="agent.id"
            class="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-solid border-n-weak"
          >
            <input
              :id="`omnup-agent-${agent.id}`"
              v-model="agent.secili"
              type="checkbox"
              class="mt-0.5 shrink-0"
              :disabled="!agent.acik && !agent.secili"
            />
            <label
              :for="`omnup-agent-${agent.id}`"
              class="flex flex-col gap-0.5 min-w-0 cursor-pointer"
            >
              <span class="flex items-center gap-2 text-sm text-n-slate-12">
                {{ agent.baslik }}
                <span
                  class="px-1.5 py-0.5 text-[10px] rounded bg-n-alpha-2 text-n-slate-11"
                >
                  {{ agent.yontem }}
                </span>
                <span v-if="!agent.acik" class="text-[10px] text-n-ruby-11">
                  {{ t('OMNUP_AGENT.PASIF') }}
                </span>
              </span>
              <span v-if="agent.aciklama" class="text-xs text-n-slate-11">
                {{ agent.aciklama }}
              </span>
              <span class="text-xs truncate text-n-slate-10">
                {{ agent.adres }}
              </span>
            </label>
          </li>
        </ul>

        <div>
          <Button
            size="sm"
            variant="ghost"
            icon="i-lucide-plus"
            :label="t('OMNUP_AGENT.YENI')"
            @click="olusturmayaGit"
          />
        </div>
      </template>

      <!-- Kaydet ozellik dalinin DISINDA: agentlar (custom_tools)
           kapali bir hesapta katalog ayarlari duzenlenebiliyor ama
           kaydedilemiyordu. -->
      <div>
        <Button
          size="sm"
          variant="solid"
          :label="t('OMNUP_AGENT.KAYDET')"
          :is-loading="kaydediliyor"
          :disabled="kaydediliyor || !degisti"
          @click="kaydet"
        />
      </div>
    </template>
  </div>
</template>
